"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { DialogProps } from "@/components/ui/dialog";
import Link from "next/link";
import { useRouter } from "next-nprogress-bar";
import { Roboto } from "next/font/google";
import { cn } from "@/lib/utils";
import { googleSignIn, azureSignIn, yahooSignIn } from "@/lib/signInUtils";
import { useState, useEffect } from "react";
import { Mail } from "lucide-react";
import {
  OAuthProviderButtons,
  type OAuthProviderOption,
  GoogleLogo,
  MicrosoftLogo,
  YahooLogo,
} from "@/components/ui/oauthProviderButtons";
import EmailAuthForm from "@/components/ui/EmailAuthForm";

const robotoFonts = Roboto({
  weight: ["400"],
  subsets: ["latin"],
  display: "swap",
  style: "normal",
});

type Props = DialogProps & {
  headerText: string;
  callbackUrl?: string;
};

const GoogleAuthModal: React.FC<Props> = ({
  onOpenChange,
  headerText,
  callbackUrl,
  open,
  ...props
}) => {
  const router = useRouter();
  const [showEmailAuth, setShowEmailAuth] = useState(false);
  // Mirror the OAuth in-flight state so Email greys out alongside the providers
  // — otherwise three buttons dim and Email stays bright white, reading as a
  // bug rather than a single pending state. OAuthProviderButtons reports
  // pending=false on unmount (e.g. switching to the email view), so this resets
  // itself; the 15s recovery timer re-enables everything if an attempt hangs.
  const [oauthPending, setOauthPending] = useState(false);

  const iconClass = "h-5 w-5 flex-shrink-0";
  const providers: OAuthProviderOption[] = [
    {
      id: "google",
      label: "Continue with Google",
      icon: <GoogleLogo className={iconClass} />,
      signIn: () => googleSignIn(callbackUrl),
    },
    {
      id: "azure",
      label: "Continue with Microsoft",
      icon: <MicrosoftLogo className={iconClass} />,
      signIn: () => azureSignIn(callbackUrl),
    },
    {
      id: "yahoo",
      label: "Continue with Yahoo",
      icon: <YahooLogo className={iconClass} />,
      signIn: () => yahooSignIn(callbackUrl),
    },
  ];

  // Reset email auth state when modal is opened. (OAuth loading/error state
  // lives in OAuthProviderButtons, which unmounts whenever we leave this view —
  // switching to / from the email form — so it resets itself.)
  useEffect(() => {
    if (open) {
      setShowEmailAuth(false);
    }
  }, [open]);

  const handleBackToOptions = () => {
    setShowEmailAuth(false);
  };

  // Email Authentication View
  if (showEmailAuth) {
    return (
      <Dialog
        onOpenChange={
          onOpenChange ? onOpenChange : () => router.replace("/?refetch=true")
        }
        open={open}
        {...props}
      >
        <DialogContent>
          <EmailAuthForm
            onBack={handleBackToOptions}
            callbackUrl={callbackUrl}
            title={headerText}
            buttonText="Get Started"
            showTerms={true}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // Main Sign-in Options View
  return (
    <Dialog
      onOpenChange={
        onOpenChange ? onOpenChange : () => router.replace("/?refetch=true")
      }
      open={open}
      {...props}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{headerText}</DialogTitle>
          <DialogDescription>
            An account is required to get started.
          </DialogDescription>
        </DialogHeader>
        <div className="flex w-full flex-col items-center justify-center gap-y-4 py-1">
          <OAuthProviderButtons
            providers={providers}
            errorMessage="Couldn't connect. Try again, or continue with email."
            onPendingChange={setOauthPending}
          />
          {/* Email greys out while a provider round-trip is in flight so the
              modal reads as one pending state; it re-enables on recovery. */}
          <Button
            variant="outline"
            onClick={() => setShowEmailAuth(true)}
            disabled={oauthPending}
            className="font-roboto flex w-[240px] justify-start gap-2.5 border border-input bg-pure-white px-3 py-2.5 font-medium tracking-[0.25px]"
          >
            <Mail className="h-5 w-5 text-pure-black/60" />
            <span className={cn("text-pure-black", robotoFonts.className)}>
              Continue with Email
            </span>
          </Button>
        </div>
        <div className="px-6 pt-2 text-center text-xs text-muted-foreground">
          By creating an account you are accepting Gondola&apos;s{" "}
          <Link
            href="/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Privacy Policy
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GoogleAuthModal;
